import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Modal } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { getCanDownloadNode, sortDataRoomNodes } from 'utils';
import FileCheckSelector from '../../../FileCheckSelector';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { DataRoomSelectableNode } from '../../../FileCheckSelector/types';
import { collectCheckedNodesForDownload } from '../../../FileCheckSelector/utils';
import useNodes from 'hooks/useNodes';
import { AxiosResponse } from 'axios';

import './DownloadModal.styles.scss';

type DownloadModalProps = {
  className?: string;
  isLoading?: boolean;
  initiallyCheckedNodes?: string[];
  nodes?: DataRoomSelectableNode[];
  onDownload?: (dataRoomNodes: DataRoomNode[]) => void;
  onClose?: () => void;
  getChildren: (parentId?: string) => Promise<AxiosResponse<DataRoomNode[]>>;
};

const DownloadModal: React.ForwardRefRenderFunction<
  ModalRef,
  DownloadModalProps
> = (props, ref) => {
  const {
    className,
    nodes,
    isLoading,
    initiallyCheckedNodes = [],
    getChildren,
    onDownload,
    onClose,
  } = props;

  const classes = classNames('download-modal', className);

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const [dataRoomNodes, setDataRoomNodes] = useState<DataRoomSelectableNode[]>(
    [],
  );

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const { getCopyByNodeType } = useNodes(nodes);

  const title = getCopyByNodeType(
    'confirmDownloadingItems',
    'confirmDownloadingFolders',
    'confirmDownloadingFiles',
  );

  const description = getCopyByNodeType(
    'youHaveDownloadPermissionForItems',
    'youHaveDownloadPermissionForFolders',
    'youHaveDownloadPermissionForFiles',
  );

  const checkedNodes = useMemo(
    () => collectCheckedNodesForDownload(dataRoomNodes),
    [dataRoomNodes],
  );

  const checkIsNodeDisabled = useCallback(
    (node: DataRoomNode) => !getCanDownloadNode(node),
    [],
  );

  const onDownloadFiles = useCallback(
    () => onDownload(checkedNodes),
    [checkedNodes, onDownload],
  );

  useEffect(() => {
    if (!modal?.openState || isLoading) return;

    const sortedNodes = sortDataRoomNodes(nodes);

    setDataRoomNodes(sortedNodes);

    return () => {
      setDataRoomNodes([]);
    };
  }, [nodes, modal, isLoading, checkIsNodeDisabled]);

  return (
    <Modal
      className={classes}
      title={<h2>{title}</h2>}
      ref={modalRef}
      onClose={onClose}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <p>{description}</p>
          <FileCheckSelector
            canShareBuyerFiles
            initiallyCheckedNodes={initiallyCheckedNodes}
            dataRoomNodes={dataRoomNodes}
            disabledLabel={t('viewOnlyLc')}
            setDataRoomNodes={setDataRoomNodes}
            checkIsNodeDisabled={checkIsNodeDisabled}
            getChildren={getChildren}
          />
        </>
      )}

      <div className="download-modal__actions">
        <Button variant="link" onClick={modal?.close}>
          {t('cancel')}
        </Button>
        <Button onClick={onDownloadFiles} disabled={!checkedNodes.length}>
          {t('download')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(DownloadModal);
